import { useMemo } from 'react';
import './AgentNetworkFlow.css';

interface AgentNetworkFlowProps {
  /** The prompt the external user sends in. */
  question?: string;
  /** The answer that travels back to the user. */
  answer?: string;
  /** Labels for the first hidden layer (the working agents). */
  agents?: string[];
  /** Caption under the core. */
  coreLabel?: string;
  className?: string;
}

const DEFAULT_AGENTS = ['Retrieve', 'Analyze', 'Validate'];

interface Pt {
  x: number;
  y: number;
}

const line = (a: Pt, b: Pt) => `M${a.x},${a.y} L${b.x},${b.y}`;

/**
 * Animated end-to-end agent flow:
 *  user prompt  ->  flows into a neural-network "Agent Core"  ->
 *  several agents process in waves  ->  the combined result returns to the user.
 * Pure SVG + CSS, themeable, reduced-motion aware.
 */
export function AgentNetworkFlow({
  question = 'do the RR',
  answer = 'RR report ready',
  agents = DEFAULT_AGENTS,
  coreLabel = 'Multi-Agent Reasoning Core',
  className,
}: AgentNetworkFlowProps) {
  const model = useMemo(() => {
    const input: Pt = { x: 120, y: 90 };
    const output: Pt = { x: 270, y: 90 };

    const n = agents.length;
    const top = 56;
    const span = 68;
    const hidden1: Pt[] = agents.map((_, i) => ({
      x: 168,
      y: n === 1 ? 90 : top + (span / (n - 1)) * i,
    }));
    const hidden2: Pt[] = [60, 90, 120].map((y) => ({ x: 216, y }));

    const meshA = hidden1.flatMap((h) => [line(input, h)]);
    const meshB = hidden1.flatMap((a) => hidden2.map((b) => line(a, b)));
    const meshC = hidden2.map((b) => line(b, output));

    return { input, output, hidden1, hidden2, edges: [...meshA, ...meshB, ...meshC], meshA, meshC };
  }, [agents]);

  return (
    <div className={`anf${className ? ` ${className}` : ''}`}>
      <svg
        className="anf__svg"
        viewBox="0 0 360 180"
        role="img"
        aria-label="A user prompt flows into a multi-agent neural core which processes it and returns a result"
        preserveAspectRatio="xMidYMid meet"
      >
        <defs>
          <radialGradient id="anfNode" cx="50%" cy="40%" r="60%">
            <stop offset="0%" stopColor="#818cf8" />
            <stop offset="100%" stopColor="#4338ca" />
          </radialGradient>
          <linearGradient id="anfReq" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#4f46e5" stopOpacity="0.1" />
            <stop offset="100%" stopColor="#6366f1" stopOpacity="0.7" />
          </linearGradient>
        </defs>

        {/* ---------- External user ---------- */}
        <g className="anf__user">
          <circle className="anf__user-bg" cx="30" cy="90" r="15" />
          <circle cx="30" cy="85" r="4.4" fill="#fff" />
          <path d="M21 99c0-5 4-8 9-8s9 3 9 8" fill="#fff" />
        </g>

        {/* Request bubble */}
        <g className="anf__bubble anf__bubble--req">
          <rect x="6" y="30" width="78" height="22" rx="7" />
          <path d="M26 52 l5 7 l5 -7 z" className="anf__bubble-tail" />
          <text x="45" y="44" className="anf__bubble-text">{question}</text>
        </g>

        {/* Response bubble (fades in each cycle) */}
        <g className="anf__bubble anf__bubble--res">
          <rect x="6" y="128" width="84" height="22" rx="7" />
          <path d="M26 128 l5 -7 l5 7 z" className="anf__bubble-tail anf__bubble-tail--res" />
          <text x="48" y="142" className="anf__bubble-text anf__bubble-text--res">✓ {answer}</text>
        </g>

        {/* ---------- Core panel ---------- */}
        <rect className="anf__panel" x="100" y="34" width="184" height="112" rx="14" />
        <text x="192" y="166" className="anf__core-label">{coreLabel}</text>

        {/* Request arrow into the core */}
        <path id="anf-req-path" className="anf__flow-line anf__flow-line--req" d="M46,90 L112,90" />
        <circle className="anf__flow-pulse" r="2.4">
          <animateMotion dur="2s" repeatCount="indefinite" path="M46,90 L112,90" />
        </circle>

        {/* Edges */}
        {model.edges.map((d, i) => (
          <path key={`e-${i}`} className="anf__edge" d={d} />
        ))}

        {/* Processing pulses on a representative subset of edges */}
        {model.meshA.map((d, i) => (
          <circle
            key={`pa-${i}`}
            className="anf__pulse"
            r="1.8"
            style={{ animationDelay: `${i * 0.25}s` }}
          >
            <animateMotion dur="1.6s" repeatCount="indefinite" begin={`${i * 0.25}s`} path={d} />
          </circle>
        ))}
        {model.meshC.map((d, i) => (
          <circle
            key={`pc-${i}`}
            className="anf__pulse"
            r="1.8"
            style={{ animationDelay: `${0.8 + i * 0.25}s` }}
          >
            <animateMotion dur="1.6s" repeatCount="indefinite" begin={`${0.8 + i * 0.25}s`} path={d} />
          </circle>
        ))}

        {/* Nodes — pulse in left-to-right waves */}
        <g>
          <circle className="anf__node anf__node--io" cx={model.input.x} cy={model.input.y} r="6" style={{ animationDelay: '0s' }} />
          {model.hidden1.map((h, i) => (
            <g key={`h1-${i}`}>
              <circle className="anf__node" cx={h.x} cy={h.y} r="5.5" style={{ animationDelay: `${0.4 + i * 0.12}s` }} />
              <text x={h.x} y={h.y - 9} className="anf__node-label">{agents[i]}</text>
            </g>
          ))}
          {model.hidden2.map((h, i) => (
            <circle key={`h2-${i}`} className="anf__node" cx={h.x} cy={h.y} r="5" style={{ animationDelay: `${0.8 + i * 0.12}s` }} />
          ))}
          <circle className="anf__node anf__node--io" cx={model.output.x} cy={model.output.y} r="6" style={{ animationDelay: '1.2s' }} />
        </g>

        {/* Return path: combined result flows back to the user */}
        <path id="anf-ret-path" className="anf__flow-line anf__flow-line--ret" d="M270,96 C270,165 120,168 40,118" />
        <circle className="anf__flow-pulse anf__flow-pulse--ret" r="2.4">
          <animateMotion dur="2.4s" repeatCount="indefinite" begin="1.4s" path="M270,96 C270,165 120,168 40,118" />
        </circle>
      </svg>
    </div>
  );
}
